import { Alert, Autocomplete, Button, Checkbox, FormControl, FormControlLabel, FormGroup, Grid, TextField } from '@mui/material';
import { FormEvent, SyntheticEvent, useContext, useState } from 'react';
import { Store } from '../../../app/services/api/generated';
import { useTranslation } from 'react-i18next';
import { base64ToBlob, exportFile, filterStoreOptions } from '../../../utils';
import { UserContext } from '../../../components/shared/useUser';
import useLogError from '../../../hooks/useLogError';
import { DeliveryReportRequest, client } from '../../../app/services/api/orderManagementClient';
import { StoreScope } from '../components/StoreScopePicker';
import AdvancedFilters from '../components/AdvancedFilters';

type IProps = {
  /** Fired after a report is successfully queued so the page can refresh status panels. */
  onGenerated?: () => void;
  onGenerationFailed?: (message?: string) => void;
  storeScope?: StoreScope;
  /** Full scope pickers rendered inside the merged Advanced filters collapse. */
  advancedScopeContent?: React.ReactNode;
}

const DeliveryReportForm = (props: IProps) => {
  const { user, selectedStore } = useContext(UserContext);
  const [selectedStores, setSelectedStores] = useState<Store[]>([]);
  const [days, setDays] = useState<number>(10);
  const [errorMessage, setErrorMessage] = useState<string>('');
  // Flipped on failed submit validation so required fields render their error state.
  const [showErrors, setShowErrors] = useState<boolean>(false);
  const [isGenerating, setIsGenerating] = useState<boolean>(false);
  const [checkedAllStores, setCheckedAllStores] = useState<boolean>(false);
  const { logError } = useLogError();
  const { t } = useTranslation('common');

  const currentFranchiseStores = props.storeScope
    ? (user?.stores?.filter(x => x.franchiseId === props.storeScope!.franchiseId) ?? [])
    : (user?.stores?.filter(x => x.franchiseId === selectedStore?.franchiseId) ?? []);

  // Scoped: empty storeNumbers means all stores of the scope's franchise.
  const scopeStoreNumbers = props.storeScope
    ? (props.storeScope.storeNumbers.length > 0
      ? props.storeScope.storeNumbers
      : currentFranchiseStores.map(x => x.storeNumber as string))
    : null;

  const handleSelectAllChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    const checked = event.target.checked;
    setSelectedStores([]);
    setCheckedAllStores(checked);
  };

  const handleSelectChange = (event: SyntheticEvent, value: Store[]) => {
    setSelectedStores(value);
  };

  const handleSubmit = (event: FormEvent) => {
    event.preventDefault();
    // Days is required — submitting without it sends a bad request.
    if (days < 1) {
      setShowErrors(true);
      setErrorMessage(t('reportingV2.days-required'));
      return;
    }
    setIsGenerating(true);
    setErrorMessage('');

    const storeNumbers = scopeStoreNumbers ?? (checkedAllStores
      ? currentFranchiseStores.map(x => x.storeNumber as string)
      : selectedStores.map(x => x.storeNumber as string));

    const request: DeliveryReportRequest = {
      days: days,
      storeNumbers: storeNumbers ?? [],
    };

    // OM API requires a storeNumber path param for context; use the first resolved store.
    const contextStore = props.storeScope ? storeNumbers[0] ?? '' : selectedStore?.storeNumber ?? '';

    client.generateDeliveryReport(contextStore, request).then((result) => {
      if (!result.success) {
        props.onGenerationFailed?.(result.message);
        return;
      }
      exportFile(base64ToBlob(result.data.fileData), 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet', result.data.fileName);
      props.onGenerated?.();
    }).catch((error) => {
      props.onGenerationFailed?.(typeof(error) === 'string' ? error : undefined);
      logError(error);
    }).finally(() => {
      setIsGenerating(false);
    });
  };

  return (
    <form onSubmit={handleSubmit}
      noValidate>
      <Grid container
        spacing={10}
        sx={{ pt: '10px' }}>
        {!props.storeScope && (
          <>
            <Grid item
              sm={6}
              xs={12}>
              <FormControl fullWidth>
                <Autocomplete
                  fullWidth
                  multiple
                  limitTags={2}
                  options={currentFranchiseStores}
                  value={selectedStores}
                  onChange={handleSelectChange}
                  disabled={checkedAllStores}
                  getOptionKey={(option) => option.storeNumber ?? ''}
                  getOptionLabel={(option) => option.storeName ?? ''}
                  filterOptions={filterStoreOptions}
                  renderInput={(params) => (
                    <TextField {...params}
                      label={t('reporting.select-stores')}
                      placeholder={t('reporting.select-stores')} />
                  )}
                />
              </FormControl>
            </Grid>
            <Grid item
              sm={6}
              xs={12}>
              <FormGroup style={{ textAlign: 'right' }}>
                <FormControlLabel
                  control={<Checkbox
                    checked={checkedAllStores}
                    onChange={handleSelectAllChange} />}
                  label={t('reporting.all-stores')}
                />
              </FormGroup>
            </Grid>
          </>
        )}
        <Grid item
          xs={12}>
          <AdvancedFilters>
            <Grid container
              spacing={10}>
              {props.advancedScopeContent && (
                <Grid item
                  xs={12}>
                  {props.advancedScopeContent}
                </Grid>
              )}
              <Grid item
                sm={6}
                xs={12}>
                <TextField
                  fullWidth
                  type="number"
                  error={showErrors && days < 1}
                  label={t('reporting.delivery-days')}
                  value={days}
                  onChange={(e) => setDays(Math.max(1, Math.min(30, parseInt(e.target.value) || 10)))}
                  inputProps={{ min: 1, max: 30 }}
                />
              </Grid>
            </Grid>
          </AdvancedFilters>
        </Grid>
        {
          errorMessage && 
          <Grid item
            xs={12}>
            <Alert severity="error">{errorMessage}</Alert>
          </Grid>
        }
        <Grid item
          xs={12}
          sx={{ display: 'flex', justifyContent: 'right', columnGap: '10px' }}>
          <Button size='lg'
            disabled={
              isGenerating ||
              (props.storeScope !== undefined && props.storeScope.franchiseId == null)
            }
            type='submit'>{t('reporting.generate')}</Button>
        </Grid>
      </Grid>
    </form>
  );
};

export default DeliveryReportForm;
